// 主題偏好(spec 16 §6.1「強制支援深色模式…預設跟隨系統」)。
// 三態:system(跟隨 OS,預設)/ light / dark。
//  - system → 移除 html[data-theme],交給 tokens.css 的 @media prefers-color-scheme
//  - light/dark → 設 html[data-theme] 覆寫系統
// 持久化 localStorage('ls.theme'),與 i18n('ls.lang')同一套模式(see i18n/index.ts)。
export const THEME_PREFS = [
  { value: 'system', labelKey: 'account.themeSystem' },
  { value: 'dark', labelKey: 'account.themeDark' },
  { value: 'light', labelKey: 'account.themeLight' },
] as const;

export type ThemePref = (typeof THEME_PREFS)[number]['value'];

export const THEME_STORAGE_KEY = 'ls.theme';
export const DEFAULT_THEME: ThemePref = 'system';

function isPref(v: string | null | undefined): v is ThemePref {
  return v === 'system' || v === 'light' || v === 'dark';
}

/** 初始主題偏好:localStorage(使用者選定)→ 預設跟隨系統。 */
export function detectInitialTheme(): ThemePref {
  try {
    const saved = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    if (isPref(saved)) return saved;
  } catch {
    /* SSR / 隱私模式 */
  }
  return DEFAULT_THEME;
}

// ls-loop 2026-10-11：深色 = 外觀 A「監控室」、淺色 = 外觀 B「居家」（tokens.css）。
// 版面差異（B 導覽在上、標題字體）靠 <html data-look="control"|"home">（global.css）：
// 跟隨系統時這裡依 OS 解析，OS 切深淺時跟著換。
export type Look = 'control' | 'home';

const lightQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: light)')
    : null;

/** 偏好 → 實際外觀（system 看 OS）。 */
export function resolveLook(pref: ThemePref): Look {
  if (pref === 'light') return 'home';
  if (pref === 'dark') return 'control';
  return lightQuery()?.matches ? 'home' : 'control';
}

let currentPref: ThemePref = DEFAULT_THEME;
let osListening = false;

/** 套用偏好到 <html data-theme / data-look>(不持久化)。boot 與切換共用。 */
export function applyTheme(pref: ThemePref): void {
  if (typeof document === 'undefined') return;
  currentPref = pref;
  const el = document.documentElement;
  if (pref === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', pref);
  el.setAttribute('data-look', resolveLook(pref));
  const q = lightQuery();
  if (q && !osListening && typeof q.addEventListener === 'function') {
    osListening = true;
    q.addEventListener('change', () => {
      if (currentPref === 'system') document.documentElement.setAttribute('data-look', resolveLook('system'));
    });
  }
}

/** 帳號上存的外觀（偏好 blob 的 theme）：合法就套用並記到本機；登入後 AppShell 叫一次。 */
export function adoptAccountTheme(v: unknown): ThemePref | null {
  if (typeof v !== 'string' || !isPref(v) || v === currentPref) return null;
  setTheme(v);
  return v;
}

/** 切換主題並持久化(ThemeSwitcher 用)。 */
export function setTheme(pref: ThemePref): void {
  applyTheme(pref);
  try {
    globalThis.localStorage?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* SSR / 隱私模式 → 退化為本 session 記憶 */
  }
}
